import { useState, useMemo, useCallback } from "react";
import { Star as StarIcon } from "lucide-react";

export default function StarRating({ rating = 0, onRatingChange, allowClear = true }) {
  const [hover, setHover] = useState(null);

  const display = useMemo(() => (hover != null ? hover : rating), [hover, rating]);

  const setValue = useCallback(
    (val) => {
      if (allowClear && val === rating) {
        onRatingChange?.(0);
      } else {
        onRatingChange?.(val);
      }
    },
    [allowClear, rating, onRatingChange]
  );

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      setValue(Math.min(5, (rating || 0) + 1));
    }
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      setValue(Math.max(0, (rating || 0) - 1));
    }
    if (e.key === "Home") {
      e.preventDefault();
      setValue(0);
    }
    if (e.key === "End") {
      e.preventDefault();
      setValue(5);
    }
    if (e.key === "Enter" && hover != null) {
      setValue(hover);
    }
  };

  return (
    <div
      className="inline-flex items-center gap-1"
      role="slider"
      aria-label="Note"
      aria-valuemin={0}
      aria-valuemax={5}
      aria-valuenow={rating}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= display;
        return (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHover(star)}
            onClick={() => setValue(star)}
            className="rounded p-0.5 outline-none transition-transform hover:scale-[1.05] focus-visible:ring-2 focus-visible:ring-accent/40"
            aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            title={`${star} / 5`}
          >
            <StarIcon
              className={`w-6 h-6 ${active ? "text-yellow-400" : "text-faint"}`}
              style={active ? { fill: "#facc15" } : {}}
            />
          </button>
        );
      })}
      {allowClear && rating > 0 && (
        <button
          type="button"
          onClick={() => onRatingChange?.(0)}
          className="ml-2 text-xs text-muted underline hover:text-ink"
        >
          Clear
        </button>
      )}
    </div>
  );
}
